import type { LhcItemDetail } from '@diamond/shared/types';
import { formatCurrencyIdr } from '@diamond/shared/utils';

const EMPTY_DISPLAY = '-';

export function resolveMutationDisplayAnchorIds(
  items: LhcItemDetail[]
): Set<string> {
  const anchorByOrderMeta = new Map<string, LhcItemDetail>();

  for (const item of items) {
    const orderMetaId = item.order_meta_id;
    if (!orderMetaId) {
      continue;
    }

    const current = anchorByOrderMeta.get(orderMetaId);
    if (!current || item.display_order < current.display_order) {
      anchorByOrderMeta.set(orderMetaId, item);
    }
  }

  return new Set([...anchorByOrderMeta.values()].map((item) => item.id));
}

export function isMutationDisplayAnchor(
  item: LhcItemDetail,
  anchorIds: Set<string>
): boolean {
  if (!item.order_meta_id) {
    return true;
  }

  return anchorIds.has(item.id);
}

type MutationDisplayFields = {
  mutationValue: string;
  differenceAmount: string;
  bankMutationNo: string;
  bankMutations: Array<{
    bankMutationId: string;
    bankMutationNo: string;
  }>;
  groupTotalInvoiceAmount: string;
};

export function resolveMutationDisplayFields(
  item: LhcItemDetail
): MutationDisplayFields {
  const mutationGroup = item.mutation_group;
  if (mutationGroup) {
    const bankMutations = mutationGroup.bank_mutations.map((mutation) => ({
      bankMutationId: mutation.bank_mutation_id,
      bankMutationNo: mutation.bank_mutation_no,
    }));

    return {
      mutationValue: formatCurrencyIdr(mutationGroup.total_mutation_amount),
      differenceAmount: formatCurrencyIdr(mutationGroup.difference_amount),
      bankMutationNo:
        bankMutations.map((mutation) => mutation.bankMutationNo).join(', ') ||
        EMPTY_DISPLAY,
      bankMutations,
      groupTotalInvoiceAmount: formatCurrencyIdr(
        mutationGroup.total_invoice_amount
      ),
    };
  }

  const bankMutations =
    item.bank_mutations?.map((mutation) => ({
      bankMutationId: mutation.bank_mutation_id,
      bankMutationNo: mutation.bank_mutation_no,
    })) ?? [];

  return {
    mutationValue: formatCurrencyIdr(item.mutation_amount),
    differenceAmount: formatCurrencyIdr(item.difference_amount),
    bankMutationNo:
      bankMutations.map((mutation) => mutation.bankMutationNo).join(', ') ||
      EMPTY_DISPLAY,
    bankMutations,
    groupTotalInvoiceAmount: formatCurrencyIdr(item.invoice_amount),
  };
}

export function shouldShowMutationDisplayOnRow(
  data: { isChildRow?: boolean; isMutationDisplayAnchor?: boolean } | undefined
): boolean {
  if (!data || data.isChildRow) {
    return true;
  }

  return data.isMutationDisplayAnchor !== false;
}
